import DefaultProductLogo from "@assets/images/logo/default-product-logo.png";
import { DeleteButton } from "@components/buttons/delete";
import { PlusButton } from "@components/buttons/plus";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHAvatar,
  SHChip,
  SHStack,
  SHTableCell,
  SHTypography,
} from "@components/design-systems";
import { SHCollapse } from "@components/design-systems/sh-collapse";
import { EditSVG, PlusSVG } from "@components/svgs";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useUserPermissions } from "@hooks/userUserPermission";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import { AnalysisDecisionStatus } from "@models/platform-analysis/enums/feature/decisionStatus";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { useTheme } from "@mui/material";
import { isEmpty, isNil } from "lodash";
import { memo, useMemo } from "react";
import { useParams } from "react-router";
import { useToggle } from "react-use";
import { AnalysisDecisionCommentDialog } from "../../../../../dialogs/product-comment";
import { TableVirtualizationConfigs as Configs } from "../../../../configs";
import { getProductDecisionStatusColor } from "../../../../utils";
import { AnalysisTableDecisionBanner } from "../../../decision-banner";

export type RenderedProductHeaderCellProps = {
  product: SelectedProductDTO;
  fixedWidth?: number;
};

export const RenderedProductHeaderCell = memo(
  ({
    product,
    fixedWidth = Configs.DefaultColumnWidth,
  }: RenderedProductHeaderCellProps) => {
    const isFireFox =
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") !== -1;
    const { analysisId } = useParams<{ analysisId: string }>();
    const { palette } = useTheme();

    const {
      decisionStatus: productDecisionStatus,
      comment: productComment,
      productLogo: productLogoData,
      isProductMain,
    } = product || {};
    const {
      isShowAnalysisScores,
      isReadOnly,
      isCompleted,
      selectedStep,
      updateDecision,
    } = usePlatformAnalysis();
    const { isSupplierGroup, isAdviserGroup } = useUserPermissions();
    // #region States
    const [isSubmitting, toggleIsSubmitting] = useToggle(false);
    const [isShortlisting, toggleIsShortlisting] = useToggle(false);
    const [openCommentDialog, toggleOpenCommentDialog] = useToggle(false);
    // #endregion States

    // #region Handlers
    const setDecisionAndComment = async ({
      status = productDecisionStatus || null,
      comment = productComment?.comment || null,
      restore = false,
    }: {
      status?: AnalysisDecisionStatus | null;
      comment?: string | null;
      restore?: boolean;
    }) => {
      return await updateDecision({
        analysisId: analysisId || "",
        analysisStep: selectedStep || AnalysisStep.Feature,
        productId: product.id,
        decisionStatus: status,
        comment: restore ? null : comment,
      });
    };

    const handleOnDisqualify = () => {
      toggleIsSubmitting(true);
      setTimeout(async () => {
        await setDecisionAndComment({
          status: AnalysisDecisionStatus.Disqualified,
        });
        toggleIsSubmitting(false);
      }, 0);
    };

    const handleRestore = () => {
      toggleIsSubmitting(true);
      setTimeout(async () => {
        await setDecisionAndComment({
          status: AnalysisDecisionStatus.Undecided,
          restore: true,
        });
        toggleIsSubmitting(false);
      }, 0);
    };

    const handleShortlist = () => {
      toggleIsShortlisting(true);
      setTimeout(async () => {
        await setDecisionAndComment({
          status: AnalysisDecisionStatus.Shortlisted,
        });
        toggleIsShortlisting(false);
      }, 0);
    };

    const handleOnSubmitComment = (comment: string) => {
      toggleIsSubmitting(true);
      setTimeout(async () => {
        const response = await setDecisionAndComment({
          status: productDecisionStatus || null,
          comment,
        });
        toggleIsSubmitting(false);

        if (response) toggleOpenCommentDialog(false);
      }, 0);
    };
    // #endregion Handlers

    // #region Effects & Variables
    const productLogo = useMemo(() => {
      if (!productLogoData || !productLogoData.url) return DefaultProductLogo;

      return productLogoData.url;
    }, [productLogoData]);

    const showShortListButton = useMemo(() => {
      if (!isAdviserGroup) return false;

      return (
        productDecisionStatus === AnalysisDecisionStatus.Undecided ||
        isNil(productDecisionStatus)
      );
    }, [isAdviserGroup, productDecisionStatus]);

    const showCommentButton = useMemo(() => {
      return (
        isSupplierGroup ||
        (isAdviserGroup &&
          !!productDecisionStatus &&
          productDecisionStatus !== AnalysisDecisionStatus.Undecided)
      );
    }, [isSupplierGroup, isAdviserGroup, productDecisionStatus]);

    const showDeleteButton = useMemo(() => {
      if (!isAdviserGroup) return false;

      return productDecisionStatus !== AnalysisDecisionStatus.Disqualified;
    }, [isAdviserGroup, productDecisionStatus]);

    const showRestoreButton = useMemo(() => {
      if (!isAdviserGroup) return false;

      return productDecisionStatus === AnalysisDecisionStatus.Disqualified;
    }, [isAdviserGroup, productDecisionStatus]);

    const shouldDisableActions = useMemo(() => {
      return isReadOnly || isSubmitting || isShortlisting;
    }, [isReadOnly, isSubmitting, isShortlisting]);

    const shouldShowDecisionButtons = useMemo(
      () => !isCompleted && !isReadOnly,
      [isCompleted, isReadOnly],
    );
    // #endregion Effects & Variables

    // #region Render
    const renderMainProductBadge = () => {
      if (!isProductMain) return null;

      return (
        <SHChip
          size="small"
          className="ProductCell-main-badge"
          label={isSupplierGroup ? "Main" : "Current"}
          color={"warning"}
          variant={"outlined"}
          sx={{
            position: "absolute",
            zIndex: 1,
          }}
        />
      );
    };

    const renderCellContent = (isPseudo?: boolean) => {
      return (
        <>
          <SHStack
            justifyContent={"space-between"}
            flexGrow={1}
            paddingBottom={
              isSupplierGroup && shouldShowDecisionButtons ? "12px" : undefined
            }
          >
            <SHStack
              className="ProductCell-avatar-and-score"
              justifyContent={"space-between"}
              alignItems={"center"}
              flexGrow={1}
            >
              {/* Avatar & Name */}
              <SHStack
                className={`ProductCell-avatar ${
                  isShowAnalysisScores ? "with-score" : "without-score"
                }`}
                gap={0.5}
                padding={"12px 15px"}
                flex={1}
              >
                <SHAvatar
                  className="ProductCell-avatar-image"
                  src={!isPseudo ? productLogo : undefined}
                  isCircular={false}
                />
                <SHTypography
                  className="ProductCell-avatar-name"
                  variant="subtitle1"
                  fontSize={14}
                  fontWeight={700}
                  lineHeight={"110%"}
                  sx={{
                    transition: "all 0.3s ease",
                    wordBreak: "break-word",
                  }}
                >
                  {product.productName}
                </SHTypography>
              </SHStack>

              {/* Score */}
              <SHCollapse
                open={!!isShowAnalysisScores}
                displayArrow={false}
                collapseProps={{ unmountOnExit: true }}
              >
                <SHStack
                  className="ProductCell-score"
                  direction={"column"}
                  paddingX={"15px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  flexShrink={0}
                >
                  <SHTypography
                    className="ProductCell-score-title"
                    variant="body3"
                    colorVariant="third"
                    fontSize={13}
                    style={{ transition: "all 0.3s ease" }}
                  >
                    Overall
                  </SHTypography>
                  <SHTypography
                    className="ProductCell-score-value"
                    variant="subtitle1"
                    lineHeight={"150%"}
                    fontSize={20}
                    fontWeight={700}
                    sx={{ transition: "all 0.3s ease" }}
                  >
                    {`${product.totalScore} %`}
                  </SHTypography>
                </SHStack>
              </SHCollapse>
            </SHStack>

            {/* Decision */}
            {shouldShowDecisionButtons && (
              <SHStack paddingX={"12px"}>
                <SHStack
                  direction={"row"}
                  gap={1}
                  justifyContent={"space-between"}
                  justifyItems={"center"}
                  alignItems={"center"}
                >
                  {/* Shortlist */}
                  {showShortListButton && (
                    <PlusButton
                      className="ProductCell-decision-btn"
                      fullWidth
                      variant="outlined"
                      size="medium"
                      disabled={shouldDisableActions}
                      isLoading={isShortlisting}
                      startIcon={<PlusSVG />}
                      onClick={handleShortlist}
                    >
                      Shortlist
                    </PlusButton>
                  )}

                  {/* Comment */}
                  {showCommentButton && (
                    <SHStack
                      width={"100%"}
                      paddingX={isSupplierGroup ? "20px" : 0}
                    >
                      <PlusButton
                        className="ProductCell-decision-btn"
                        fullWidth
                        variant="outlined"
                        size="medium"
                        disabled={shouldDisableActions}
                        startIcon={
                          isEmpty(productComment?.comment) ? (
                            <PlusSVG />
                          ) : (
                            <EditSVG />
                          )
                        }
                        onClick={toggleOpenCommentDialog}
                      >
                        Comment
                      </PlusButton>
                    </SHStack>
                  )}

                  {showDeleteButton && (
                    <DeleteButton
                      className="ProductCell-delete-btn"
                      backgroundColor={"transparent"}
                      borderColor={palette.common.black}
                      disabled={shouldDisableActions}
                      isLoading={isSubmitting}
                      onClick={handleOnDisqualify}
                    />
                  )}

                  {showRestoreButton && (
                    <RollBackButton
                      className="ProductCell-restore-btn"
                      backgroundColor={"transparent"}
                      borderColor={palette.common.black}
                      disabled={shouldDisableActions}
                      isLoading={isSubmitting}
                      onClick={handleRestore}
                    />
                  )}
                </SHStack>
              </SHStack>
            )}
          </SHStack>

          {/* Decision banner */}
          {isAdviserGroup && (
            <AnalysisTableDecisionBanner
              decisionStatus={
                isSupplierGroup ? undefined : productDecisionStatus
              }
              isMissingEssential={product.isMissingEssential}
              isPseudoElement={isPseudo}
            />
          )}
        </>
      );
    };
    // #endregion Render

    return (
      <SHTableCell
        sx={{
          position: "relative",
          padding: "0px !important",
          margin: "0px !important",
          textAlign: "center",
          minWidth: fixedWidth,
          maxWidth: fixedWidth,
          border: `1px solid ${palette.secondary[100]}`,
          borderLeftWidth: "0px",
          backgroundColor: getProductDecisionStatusColor({
            product,
            isSupplierGroup,
          }),
        }}
      >
        {renderMainProductBadge()}

        {isFireFox && (
          <SHStack
            sx={{
              position: "absolute",
              height: "100%",
              width: "100%",
              top: 0,
              left: 0,
            }}
          >
            {renderCellContent()}
          </SHStack>
        )}

        <SHStack
          width={"100%"}
          height={"100%"}
          direction={"column"}
          justifyContent={"flex-start"}
          visibility={isFireFox ? "hidden" : "visible"}
          sx={{ pointerEvents: isFireFox ? "none" : "auto" }}
        >
          {renderCellContent(isFireFox)}
        </SHStack>

        {/* Comment dialog */}
        <AnalysisDecisionCommentDialog
          open={openCommentDialog}
          originalComment={productComment?.comment}
          onClose={toggleOpenCommentDialog}
          onSubmit={handleOnSubmitComment}
        />
      </SHTableCell>
    );
  },
);
